<!--
  ReservationSearchFilter.vue
  예약 검색 및 필터링 컴포넌트
  
  주요 기능:
  - 검색 조건 및 검색어 입력
  - 조회 기간 설정
  - 예약 상태 필터링
  - 진료과, 제조사 필터링
-->
<template>
  <div class="container-wrapper">
    <div class="search-filter-container table-info-filter">
      <!-- 검색어 행 -->
      <div class="search-filter-row">
        <div class="search-filter-title">
          검색 조건
        </div>
        <div class="search-filter-content">
          <select class="search-select" v-model="searchFilter.filter">
            <option value="customerName">고객명</option>
            <option value="phoneNumber">전화번호</option>
            <option value="companyName">병원명</option>
            <option value="categoryName">시술명</option>
          </select>
          <div class="search-input-container">
            <div class="search-icon">
              <i class="fa-regular fa-magnifying-glass" />
            </div>
            <input 
              type="text"
              class="search-input-text"
              v-model="searchFilter.keyword"
              @keyup.enter="handleSearch"
              placeholder="검색어를 입력하세요"
            />
          </div>
          <button
            class="search-btn"
            @click="handleSearch"
          >
            검색
          </button>
          <button
            class="init-btn"
            @click="handleReset"
          >
            초기화
          </button>
        </div>
      </div>

      <!-- 조회 기간 행 -->
      <div class="search-filter-row">
        <div class="search-filter-title">
          조회 기간
        </div>
        <div class="search-filter-content">
          <div class="date-filter-container">
            <div
              class="date-filter-item"
              :class="searchFilter.selectedDateFilter === 'all' ? 'selected' : ''"
              @click="handleDateFilter('all')"
            >
              전체
            </div>
            <div 
              v-for="item in searchFilter.dateFilters" 
              :key="item"
              class="date-filter-item"
              :class="searchFilter.selectedDateFilter === item ? 'selected' : ''"  
              @click="handleDateFilter(item)"
            >
              {{ item === 'etc' ? '기간 설정' : `최근 ${convertMonthAndYearText(item)}` }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- 기간 설정 행 -->
      <div class="search-filter-row" v-if="searchFilter.selectedDateFilter === 'etc'">
        <div class="">
          <!-- 빈 공간 -->
        </div>
        <div class="">
          <div class="date-selector-container custom">
            <n-date-picker 
              v-model:value="searchFilter.startAt" 
              type="date" 
              placeholder="시작 날짜를 선택해 주세요."
              :on-update:value="handleStartAt"
            />
            <div>~</div>
            <n-date-picker 
              v-model:value="searchFilter.endAt" 
              type="date" 
              placeholder="종료 날짜를 선택해 주세요."
              :on-update:value="handleEndAt"
            />
            <div class="filter-desc">(등록 시각 기준)</div>
          </div>
        </div>
      </div>
    
      <div class="search-filter-content">
        <div class="filter-group">
          <label>진료과</label>
          <select 
            class="warranty-status-select"
            v-model="searchFilter.selectedDept"
            @change="handleSearch"
          >
            <option value="">전체</option>
            <option 
              v-for="item in deptList" 
              :value="item.code" 
              :key="item.code"
            >
              {{ item.name }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>제조사</label>
          <select 
            class="warranty-status-select"
            v-model="searchFilter.selectedManufacturer"
            @change="handleSearch"
          >
            <option value="">전체</option>
            <option 
              v-for="item in manufacturerList" 
              :value="item._id || item.id" 
              :key="item._id || item.id"
            >
              {{ item.name || item.manufacturerName }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { convertMonthAndYearText } from '@/utils/utils';

export default {
  name: 'ReservationSearchFilter',
  props: {
    searchFilter: {
      type: Object,
      required: true
    },
    manufacturerList: {
      type: Array,
      default: () => []
    },
    categoryList: {
      type: Array,
      default: () => []
    },
    deptList: {
      type: Array,
      default: () => []
    }
  },
  emits: ['search', 'reset'],
  setup(props, { emit }) {
    // 검색 처리
    const handleSearch = () => {
      emit('search');
    };

    // 초기화 처리
    const handleReset = () => {
      emit('reset');
    };

    // 날짜 필터 처리
    const handleDateFilter = (value) => {
      if (value === props.searchFilter.selectedDateFilter) return;
      
      props.searchFilter.selectedDateFilter = value;
      
      if (value === 'all') {
        props.searchFilter.startAt = null;
        props.searchFilter.endAt = null;
      } else if (value !== 'etc') {
        props.searchFilter.startAt = getPastDatesInUnixTime(value);
        props.searchFilter.endAt = getCurrentUnixTime();
      }
      
      handleSearch();
    };

    // 시작 날짜 변경 처리
    const handleStartAt = (value) => {
      props.searchFilter.startAt = value;
      handleSearch();
    };

    // 종료 날짜 변경 처리
    const handleEndAt = (value) => {
      props.searchFilter.endAt = value;
      handleSearch();
    };

    // 현재 시간을 Unix 타임스탬프로 반환
    const getCurrentUnixTime = () => {
      const now = new Date();
      return Math.floor(now.getTime());
    };

    // 과거 날짜를 Unix 타임스탬프로 반환
    const getPastDatesInUnixTime = (value) => {
      const now = new Date();
      const pastDate = new Date(now);
      
      switch(value) {
        case 3:
          pastDate.setMonth(now.getMonth() - 3);
          break;
        case 12:
          pastDate.setMonth(now.getMonth() - 12);
          break;
        case 36:
          pastDate.setMonth(now.getMonth() - 36);
          break;
        default:
          return Math.floor(now.getTime());
      }
      
      return Math.floor(pastDate.getTime());
    };

    return {
      handleSearch,
      handleReset,
      handleDateFilter,
      handleStartAt,
      handleEndAt,
      convertMonthAndYearText
    };
  }
};
</script>

<style scoped lang="scss">
.search-filter-container {
  margin-bottom: 20px;
}

.search-filter-card {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.filter-row {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.filter-group {
  display: flex;
  flex-direction: column;
  width: 240px;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.search-input-group {
  display: flex;
  gap: 8px;
}

.search-select {
  min-width: 120px;
  height: 40px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 0 12px;
  font-size: 14px;
  
  &:focus {
    border-color: #FFCC32;
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 204, 50, 0.2);
  }
}

.search-input {
  flex: 1;
  height: 40px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 0 12px;
  font-size: 14px;
  
  &:focus {
    border-color: #FFCC32;
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 204, 50, 0.2);
  }
  
  &::placeholder {
    color: #999;
  }
}

.date-filter-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.date-quick-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.date-filter-btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  color: #666;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #FFCC32;
    color: #333;
  }
  
  &.active {
    background: #FFCC32;
    border-color: #FFCC32;
    color: #000;
    font-weight: 500;
  }
}

.date-picker-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-separator {
  color: #666;
  font-size: 14px;
}

.date-selector-container {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  
  &.custom {
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    width: 100%;
  }
}

.filter-desc {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.reset-btn {
  height: 40px;
  padding: 0 20px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  color: #666;
  
  &:hover {
    border-color: #999;
    color: #333;
  }
}

:deep(.yellow-button) {
  height: 40px;
  padding: 0 24px;
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  border-radius: 6px;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
}

:deep(.ant-picker) {
  height: 40px;
  border-radius: 6px;
  
  &:hover, &.ant-picker-focused {
    border-color: #FFCC32;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    gap: 16px;
  }
  
  .filter-group {
    min-width: auto;
  }
  
  .search-input-group {
    flex-direction: column;
  }
  
  .date-quick-buttons {
    justify-content: center;
  }
  
  .date-picker-group {
    flex-direction: column;
    align-items: stretch;
  }
  
  .date-selector-container {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .filter-actions {
    flex-direction: column;
    
    .ant-btn {
      width: 100%;
    }
  }
}
</style> 